import { createClient } from '@supabase/supabase-js';
import { createLibrary } from './library.js';
import { createCloudLibrary } from './cloud-library.js';
import { createDraftJournal } from './draft-journal.js';
import { escapeHtml as esc } from './document.js';
import { withRequestTimeout } from './request-timeout.js';
import './account.css';

export const account = { user: null, library: null, workspace: null, editor: null, cloud: false };

export async function startAccounts() {
  const app = document.querySelector('#app');
  const panel = document.createElement('section');
  panel.className = 'account-screen';
  panel.setAttribute('aria-label', 'Linework account');
  document.body.append(panel);
  let client, drafts, mounted = false, busy = false, currentUser = null, recovering = false;
  const local = createLibrary();
  const url = import.meta.env.VITE_SUPABASE_URL;
  const key = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
  // The module lives in src/ (development) or assets/ (built app). Deriving the
  // app root here also handles an address opened without its trailing slash.
  const moduleUrl = new URL(import.meta.url);
  const redirectTo = new URL('../', moduleUrl).href;

  function show(content, title = 'Your diagrams, wherever you work') {
    app.inert = true;
    app.style.visibility = 'hidden';
    panel.hidden = false;
    panel.innerHTML = `<div class="account-card"><a class="account-brand" href="/">Linework<span>Agent architecture canvas</span></a><h1>${esc(title)}</h1>${content}<p class="account-message" role="status" aria-live="polite"></p>${mounted ? '<button class="account-link" data-account-action="close">Back to diagram</button>' : ''}</div>`;
  }
  function message(text, error = false) {
    const element = panel.querySelector('.account-message');
    if (element) { element.textContent = text; element.classList.toggle('is-error', error); }
  }
  function close() { panel.hidden = true; app.inert = false; app.style.visibility = ''; }
  function fields(mode) {
    const recovery = mode === 'update-password', reset = mode === 'reset';
    const signup = mode === 'signup';
    show(`<p>${recovery ? 'Choose a new password for your account.' : reset ? 'We’ll email you a link to reset your password.' : 'Sign in to save diagrams and folders to your account and open them on another device.'}</p>
      <form data-account-form="${mode}">
      ${recovery ? '' : '<label>Email<input name="email" type="email" required autocomplete="email" placeholder="you@example.com"></label>'}
      ${reset ? '' : `<label>Password<input name="password" type="password" required ${signup || recovery ? 'minlength="8"' : ''} autocomplete="${signup || recovery ? 'new-password' : 'current-password'}"></label>`}
      <button type="submit">${recovery ? 'Save new password' : reset ? 'Send reset link' : signup ? 'Create account' : 'Sign in'}</button></form>
      ${recovery ? '' : `<div class="account-links"><button class="account-link" data-account-action="${signup || reset ? 'signin' : 'signup'}">${signup || reset ? 'Back to sign in' : 'Create an account'}</button>${!reset ? '<button class="account-link" data-account-action="reset">Forgot password?</button>' : ''}<button class="account-link" data-account-action="local">Use this browser only</button></div>`}`,
      recovery ? 'Reset your password' : signup ? 'Create your Linework account' : reset ? 'Forgot your password?' : 'Welcome to Linework');
  }
  async function mount(user) {
    if (mounted) {
      if ((user?.id || null) !== currentUser) location.reload();
      return;
    }
    show('<p>Loading your workspace…</p>', 'Opening Linework');
    currentUser = user?.id || null;
    account.user = user;
    account.cloud = Boolean(user);
    if (user) {
      let tabId;
      try {
        tabId = sessionStorage.getItem('linework-tab-id') || crypto.randomUUID();
        sessionStorage.setItem('linework-tab-id', tabId);
      } catch { tabId = crypto.randomUUID(); }
      drafts = createDraftJournal({ userId: user.id, tabId });
      account.library = createCloudLibrary({ client, userId: user.id, drafts });
    } else account.library = local;
    mounted = true;
    close();
    try { await import('./main.js'); }
    catch (error) { show('<button data-account-action="reload">Try again</button>', 'Linework could not start'); message(error.message, true); }
  }
  async function openAccount() {
    if (!account.cloud) {
      if (client) fields('signin');
      else unconfigured();
      return;
    }
    const shared = account.workspace?.shared;
    show(`<p>Signed in as <strong>${esc(account.user.email || '')}</strong>.</p>
      <p>${shared ? "This shared copy saves to the collaboration server. Return to My diagrams in the Live menu to manage personal diagrams and recovery drafts." : "Diagrams and folders are saved to this account. Open the same account on another device to continue."}</p>
      <div class="account-actions">${shared ? "" : '<button data-account-action="import-local">Copy browser diagrams to this account</button><button data-account-action="recover-drafts">Recover unsaved browser drafts</button>'}<button data-account-action="signout">Sign out</button></div>`, 'Your account');
  }
  function unconfigured() {
    show('<p>Cloud sign-in is not configured for this installation yet. Existing browser diagrams are still available.</p><button data-account-action="local">Open browser-local diagrams</button>', 'Welcome to Linework');
  }
  async function finishAndFlush() {
    if (account.editor?.hasOpenForm()) throw new Error('Save or cancel the open editor form before changing accounts.');
    account.editor?.finishEditing();
    await account.workspace?.flush();
    await drafts?.flush();
  }
  async function run(operation) {
    if (busy) return;
    busy = true;
    panel.setAttribute('aria-busy', 'true');
    panel.querySelectorAll('button').forEach(button => { button.disabled = true; });
    try { await operation(); }
    catch (error) { message(error.message || 'The account operation failed. Please try again.', true); }
    finally {
      busy = false;
      panel.removeAttribute('aria-busy');
      panel.querySelectorAll('button').forEach(button => { button.disabled = false; });
    }
  }
  panel.addEventListener('submit', event => {
    const form = event.target.closest('[data-account-form]');
    if (!form) return;
    event.preventDefault();
    void run(async () => {
      const data = new FormData(form), mode = form.dataset.accountForm;
      const email = String(data.get('email') || '').trim(), password = String(data.get('password') || '');
      await finishAndFlush();
      message('Working…');
      let result;
      if (mode === 'signin') result = await client.auth.signInWithPassword({ email, password });
      if (mode === 'signup') result = await client.auth.signUp({ email, password, options: { emailRedirectTo: redirectTo } });
      if (mode === 'reset') result = await client.auth.resetPasswordForEmail(email, { redirectTo: `${redirectTo}?account=recovery` });
      if (mode === 'update-password') result = await client.auth.updateUser({ password });
      if (result.error) throw result.error;
      if (mode === 'update-password') {
        recovering = false;
        history.replaceState(null, '', redirectTo);
        await mount(result.data.user);
      } else if (mode === 'reset') message('If this email has an account, a reset link is on its way.');
      else if (!result.data.session) message('Check your email to confirm your account, then return here to sign in.');
      // SIGNED_IN handles the transition, outside the auth callback's lock.
    });
  });
  panel.addEventListener('click', event => {
    const action = event.target.closest('[data-account-action]')?.dataset.accountAction;
    if (!action || busy) return;
    if (action === 'close') { if (!recovering) close(); return; }
    if (['signin', 'signup', 'reset'].includes(action)) { fields(action); return; }
    void run(async () => {
      if (action === 'reload') { location.reload(); return; }
      if (action === 'local') { if (mounted && !account.cloud) close(); else await mount(null); return; }
      if (action === 'signout') {
        await finishAndFlush();
        const { error } = await client.auth.signOut({ scope: 'local' });
        if (error) throw error;
      }
      if (action === 'import-local') {
        await finishAndFlush();
        message('Checking browser diagrams…');
        // Import prototype data into local storage first; that migration also keeps its original.
        const previous = JSON.parse(localStorage.getItem('architecture-obsidian-preview-v1') || 'null');
        if (previous) await local.migratePrototype(previous.doc, previous.view);
        const snapshot = await local.exportForCloud();
        if (!snapshot.canvases.length && !snapshot.folders.length) { message('No browser-local diagrams or folders were found at this address.'); return; }
        show(`<p>Copy ${snapshot.canvases.length} browser diagram(s) and ${snapshot.folders.length} folder(s) into <strong>${esc(account.user.email)}</strong>?</p><p>Local originals stay in this browser. Previously copied diagrams are skipped, including those moved to trash. Existing cloud edits are preserved.</p><button data-account-action="confirm-import">Copy to my account</button>`, 'Import browser diagrams');
      }
      if (action === 'confirm-import') {
        message('Copying diagrams and folders…');
        const count = await account.library.importLocal(await local.exportForCloud());
        await account.workspace.init();
        message(`${count} diagram(s) copied. Local originals are unchanged.`);
      }
      if (action === 'recover-drafts') {
        await finishAndFlush();
        const entries = await drafts.list();
        if (!entries.length) { message('No unsaved drafts were found for this account.'); return; }
        show(`<p>${entries.length} unsaved draft(s) are available for this account in this browser. Recovery creates separate diagrams and preserves the current cloud versions.</p><button data-account-action="confirm-recovery">Recover as separate diagrams</button>`, 'Recover unsaved drafts');
      }
      if (action === 'confirm-recovery') {
        message('Recovering drafts…');
        const count = await account.library.recoverDrafts();
        await account.workspace.init();
        message(`${count} draft(s) recovered as separate diagrams.`);
      }
    });
  });
  // Account forms must not trigger the editor's document-wide keyboard shortcuts.
  panel.addEventListener('keydown', event => event.stopPropagation());
  document.addEventListener('click', event => {
    if (event.target.closest('[data-account-open]')) { event.preventDefault(); void openAccount(); }
  });
  // Runs after the editor initializes. Prompt for existing local work, without importing it into an arbitrary account.
  account.onReady = async () => {
    if (!account.cloud || account.workspace?.shared) return;
    const [localRows, pending] = await Promise.all([local.list(), drafts.list()]);
    if (pending.length) {
      await openAccount();
      message(`${pending.length} unsaved browser draft(s) can be recovered for this account.`);
    } else if (localRows.length && !await account.library.getPreference('localImportNoticeSeen')) {
      await openAccount();
      message(`${localRows.length} browser-local diagram(s) are available to copy into this account.`);
      await account.library.setPreference('localImportNoticeSeen', true);
    }
  };
  show('<p>Checking your session…</p>', 'Opening Linework');
  if (!url || !key) { unconfigured(); return; }
  try {
    if (!/^https:\/\//.test(url) && !/^http:\/\/(localhost|127\.0\.0\.1)(:|\/)/.test(url)) throw new Error('Invalid cloud service URL.');
    client = createClient(url, key, {
      auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true },
      global: { fetch: withRequestTimeout(fetch) },
    });
    recovering = new URLSearchParams(location.search).get('account') === 'recovery';
    client.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') recovering = true;
      if (mounted && (session?.user?.id || null) !== currentUser && event !== 'INITIAL_SESSION') {
        // Hide the old account immediately. The new session never reuses its library or save queue.
        app.inert = true;
        app.style.visibility = 'hidden';
        account.workspace?.dispose?.();
        account.library?.close();
        setTimeout(() => { void (drafts?.flush() || Promise.resolve()).catch(() => {}).finally(() => location.reload()); }, 0);
      } else if (!mounted && ['SIGNED_IN', 'PASSWORD_RECOVERY'].includes(event)) {
        setTimeout(() => { if (recovering) fields('update-password'); else void mount(session.user); }, 0);
      }
    });
    const { data, error } = await client.auth.getSession();
    if (error) throw error;
    if (recovering && data.session) fields('update-password');
    else if (data.session) await mount(data.session.user);
    else fields('signin');
  } catch (error) {
    show('<button data-account-action="reload">Try again</button><button data-account-action="local">Open browser-local diagrams</button>', 'Your session could not load');
    message(error.message, true);
  }
}
